import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { Description, Error, UploadFile } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ptBR } from 'date-fns/locale';
import { useFormik } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useQuery } from 'react-query';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { findWalletByName } from 'src/services/wallet';
import { debounce } from 'src/utils/general';
import * as Yup from 'yup';

import { validateCSVAsync } from './utils/asyncValidation';
import { useMonitorContext } from '../../MonitorContextProvider';
import {
  CSV_CONSTRAINTS,
  getSuccessMessage,
  processFileRejectionError,
  shouldDisplayErrorInField,
  shouldDisplayErrorInList,
} from '../../validation';

interface CreateProps {
  onCancel: () => void;
  onFinish: () => void;
}

export const Create = forwardRef(function Create(
  { onCancel, onFinish }: CreateProps,
  ref,
) {
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);
  const [nameToCheck, setNameToCheck] = useState('');
  const { auth } = useAuthContext();

  const debouncedSetNameToCheck = useCallback(
    debounce((value: string) => {
      setNameToCheck(value);
    }, 500),
    [],
  );

  const {
    handleSetFunds,
    walletDraft,
    setWalletDraft,
    document,
    setDocument,
    csvValidation,
    setCsvValidation,
  } = useMonitorContext();

  const { data: walletsWithName, isFetching: isCheckingName } = useQuery(
    ['wallet-exists', nameToCheck],
    () => findWalletByName(nameToCheck, auth?.user?.id ?? 0),
    {
      enabled: !!nameToCheck && nameToCheck.length >= 3,
      staleTime: 1000 * 60,
    },
  );

  const nameExists = !!walletsWithName?.some(
    (wallet) =>
      wallet.name.trim().toLowerCase() === nameToCheck.trim().toLowerCase(),
  );

  const FORM_VALIDATION = Yup.object().shape({
    name: Yup.string()
      .required('Nome da carteira é obrigatório')
      .min(3, 'Nome deve ter pelo menos 3 caracteres'),
    description: Yup.string().max(
      500,
      'Descrição deve ter no máximo 500 caracteres',
    ),
    createdAt: Yup.date().max(new Date(), 'Data não pode ser futura'),
    csv: Yup.mixed()
      .optional()
      .test('csv-validation', 'Arquivo CSV inválido', function () {
        return csvValidation?.isValid || !csvValidation;
      }),
  });

  const formik = useFormik({
    initialValues: {
      name: walletDraft.name || '',
      description: walletDraft.description || '',
      startDate: walletDraft.startDate || new Date(),
      csv: document,
    },
    validationSchema: FORM_VALIDATION,
    onSubmit: async (values) => {
      if (nameExists) {
        formik.setFieldError('name', 'Já existe uma carteira com esse nome');
        return;
      }

      if (csvValidation && !csvValidation.isValid) {
        return;
      }
      setWalletDraft({
        name: values.name,
        description: values.description,
        startDate: values.startDate,
      });
      if (values.csv && csvValidation?.isValid && csvValidation.data) {
        handleSetFunds(csvValidation.data);
      }
      onFinish();
      return;
    },
  });

  useEffect(() => {
    formik.setFieldValue('csv', document);

    if (!document) {
      formik.setFieldError('csv', undefined);
    }
  }, [document]);

  useImperativeHandle(ref, () => ({
    validateAndSubmit: async () => {
      const errors = await formik.validateForm();
      const isValid = Object.keys(errors).length === 0;
      if (isValid) {
        await formik.submitForm();
      } else {
        formik.setTouched({
          name: true,
          description: true,
          startDate: true,
          csv: true,
        });
      }
      return isValid;
    },
  }));

  const handleCancel = () => {
    setWalletDraft({
      name: '',
      description: '',
      startDate: null,
    });
    setDocument(null);
    setCsvValidation(null);
    onCancel();
  };

  const generateCsvTemplateExample = () => {
    const csvTemplate =
      'cnpj_fundo;subclasse;serie;quantidade\n00000000000000;única;2;20\n12.345.678/0001-00;única;1;600';
    const blob = new Blob([csvTemplate], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = window?.document?.createElement('a');
    link.href = url;
    link.download = 'exemplo_carteira.csv';
    link.click();
  };

  useEffect(() => {
    setWalletDraft({
      name: formik.values.name,
      description: formik.values.description,
      startDate: formik.values.startDate,
    });
  }, [formik.values.name, formik.values.description, formik.values.startDate]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: any) => {
      if (acceptedFiles.length) {
        const file = acceptedFiles[0];
        formik.setFieldValue('csv', file);
        setDocument(file);
        setCsvValidation(null);
        setIsValidating(true);

        try {
          const validation = await validateCSVAsync(file, 100, (progress) =>
            setValidationProgress(progress),
          );
          setCsvValidation(validation);

          if (!validation.isValid) {
            formik.setFieldError('csv', 'Arquivo CSV inválido');
            formik.setFieldTouched('csv', true, false);
          } else {
            formik.setFieldError('csv', undefined);
          }
        } catch (error) {
          setCsvValidation({
            isValid: false,
            errors: ['Erro ao validar arquivo'],
          });
          formik.setFieldError('csv', 'Erro ao validar arquivo');
        } finally {
          setIsValidating(false);
          setValidationProgress(0);
        }
      }

      if (rejectedFiles.length) {
        const error = rejectedFiles[0].errors[0];
        const validationError = processFileRejectionError(error);

        if (validationError.isSmallError) {
          setCsvValidation({
            isValid: false,
            errors: [validationError.message],
          });
        } else {
          formik.setFieldError('csv', validationError.message);
          setCsvValidation({
            isValid: false,
            errors: [validationError.message],
          });
        }
      }
    },
    [formik, setDocument, setCsvValidation],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
    },
    onDrop,
    maxFiles: 1,
    maxSize: CSV_CONSTRAINTS.MAX_FILE_SIZE,
    noDragEventsBubbling: true,
  });

  useEffect(() => {
    return () => {
      formik.resetForm();
    };
  }, []);

  const hasCsvError =
    !!formik.errors.csv || (csvValidation && !csvValidation.isValid);

  const isButtonDisabled =
    isCheckingName ||
    nameExists ||
    isValidating ||
    Object.keys(formik.errors).length > 0 ||
    !!(csvValidation && !csvValidation.isValid);

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      onChange={(e) => e.stopPropagation()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
      <Typography variant="h5" sx={{ mb: 4, mt: 1, textAlign: 'center' }}>
        Criação de Carteira
      </Typography>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
        <FormControl fullWidth>
          <TextField
            label="Nome da Carteira"
            name="name"
            placeholder="Digite o nome da carteira"
            required
            value={formik.values.name}
            onChange={(e) => {
              formik.handleChange(e);
              const value = e.target.value.trim();
              debouncedSetNameToCheck(value);
            }}
            onBlur={formik.handleBlur}
            error={
              !!(formik.touched.name && (formik.errors.name || nameExists))
            }
            helperText={
              (formik.touched.name && formik.errors.name) ||
              (nameExists ? 'Já existe uma carteira com esse nome' : '') ||
              (isCheckingName ? 'Verificando nome...' : '')
            }
          />
        </FormControl>
        <FormControl fullWidth>
          <TextField
            label="Descrição"
            name="description"
            placeholder="Breve descrição sobre os ativos ou objetivos da carteira"
            multiline
            rows={4}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={!!(formik.touched.description && formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
          />
        </FormControl>
        <FormControl>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={ptBR}>
            <DatePicker
              disableFuture
              label="Data de início da carteira"
              value={formik.values.startDate || new Date()}
              onChange={(date) => formik.setFieldValue('startDate', date)}
              slotProps={{
                textField: {
                  name: 'startDate',
                  error: !!(
                    formik.touched.startDate && formik.errors.startDate
                  ),
                  helperText:
                    formik.touched.startDate &&
                    (formik.errors.startDate as string),
                },
              }}
              sx={{
                width: '50%',
              }}
            />
          </LocalizationProvider>
        </FormControl>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        <Typography variant="body1" sx={{ my: 2.5, textAlign: 'center' }}>
          Arraste e solte seu arquivo no espaço abaixo para importar
          automaticamente os investimentos da carteira. Você também pode clicar
          na área destacada para selecionar o arquivo do seu computador.
        </Typography>

        <Box
          {...getRootProps()}
          onDragOver={(e) => {
            e.currentTarget.style.border = hasCsvError
              ? '2px dashed #d32f2f'
              : '2px dashed #1976D2';
            e.currentTarget.style.backgroundColor = hasCsvError
              ? '#ffebee'
              : '#e7eff5';
            e.currentTarget.style.transition = 'all 0.3s ease';
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.border = hasCsvError
              ? '2px dashed #d32f2f'
              : '2px dashed #ccc';
            e.currentTarget.style.backgroundColor = hasCsvError
              ? '#ffebee'
              : 'transparent';
            e.currentTarget.style.transition = 'all 0.3s ease';
          }}
          sx={{
            width: '100%',
            minHeight: 100,
            border: hasCsvError
              ? '2px dashed #d32f2f'
              : document
                ? '2px dashed #1976D2'
                : '2px dashed #ccc',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backgroundColor: hasCsvError
              ? '#ffebee'
              : document
                ? '#e7eff5'
                : 'transparent',
            '&:hover': {
              borderColor: hasCsvError ? '#d32f2f' : '#1976D2',
              backgroundColor: hasCsvError ? '#ffebee' : '#e7eff5',
            },
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              py: 6,
            }}>
            <input {...getInputProps()} />
            {isValidating ? (
              <>
                <CircularProgress size={40} />
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', px: 1, mt: 1 }}>
                  Validando arquivo... {validationProgress}%
                </Typography>
                {validationProgress > 0 && (
                  <Box sx={{ width: '80%', mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={validationProgress}
                    />
                  </Box>
                )}
              </>
            ) : document ? (
              <>
                {hasCsvError ? (
                  <Error fontSize="large" htmlColor="#d32f2f" />
                ) : (
                  <Description fontSize="large" htmlColor="#1976D2" />
                )}
                <Typography variant="body2" sx={{ textAlign: 'center', px: 1 }}>
                  {document.name}
                </Typography>
                {csvValidation?.isValid && csvValidation.data && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'success.main', mt: 1 }}>
                    {getSuccessMessage(csvValidation.data)}
                  </Typography>
                )}

                {csvValidation && shouldDisplayErrorInField(csvValidation) && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#d32f2f', mt: 1, textAlign: 'center' }}>
                    {csvValidation.errors[0]}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <UploadFile
                  fontSize="large"
                  htmlColor={hasCsvError ? '#d32f2f' : '#1976D2'}
                />
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      textDecoration: 'none',
                      fontWeight: 600,
                      color: hasCsvError ? '#d32f2f' : '#1976D2',
                    }}>
                    Escolha um arquivo CSV
                  </Typography>
                  &nbsp;ou arraste e solte aqui
                </Typography>

                {csvValidation && shouldDisplayErrorInField(csvValidation) && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#d32f2f', mt: 1, textAlign: 'center' }}>
                    {csvValidation.errors[0]}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>

        {csvValidation && shouldDisplayErrorInList(csvValidation) && (
          <Box
            sx={{
              mt: 2,
              width: '100%',
              backgroundColor: '#ffebee',
              p: 2,
              maxHeight: '300px',
              overflowY: 'scroll',
              border: '1px solid #f07e7e',
              borderRadius: '4px',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#f07e7e',
                height: '30px',
                borderRadius: '4px',
                px: 2,
              },
            }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Erros encontrados no arquivo CSV:
            </Typography>
            <TableContainer>
              <Table
                size="small"
                padding="none"
                aria-label="tabela de erros CSV">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        fontSize: 13,
                        background: '#ffeaea',
                        borderBottom: '1px solid #f07e7e',
                      }}>
                      Mensagem de erro
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvValidation.errors.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          color: '#b71c1c',
                          background: index % 2 === 0 ? '#fff6f6' : '#ffeaea',
                          borderBottom: '1px solid #f7bcbc',
                          py: 0.5,
                        }}>
                        {error}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {csvValidation &&
          !csvValidation.isValid &&
          !shouldDisplayErrorInField(csvValidation) &&
          csvValidation.errors.length > 0 && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {csvValidation.errors[0]}
            </Alert>
          )}

        <Typography variant="body1" sx={{ my: 4, textAlign: 'center' }}>
          Para baixar a planilha modelo que deve ser usada para importar os
          dados&nbsp;
          <Typography
            onClick={() => generateCsvTemplateExample()}
            component="a"
            download="exemplo_carteira.csv"
            color="#1976D2"
            sx={{ textDecoration: 'none', cursor: 'pointer' }}>
            clique aqui
          </Typography>
        </Typography>
      </Box>
      <Box
        display="flex"
        gap={2}
        mt={2}
        width="100%"
        justifyContent="space-around"
        flexDirection={{ xs: 'column', sm: 'row' }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          fullWidth
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
          }}>
          <Typography fontSize={16} fontWeight={500}>
            Cancelar
          </Typography>
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          disabled={isButtonDisabled}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
          }}>
          <Typography fontSize={16} fontWeight={500}>
            Adicionar fundos
          </Typography>
        </Button>
      </Box>
    </Box>
  );
});
